import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UserPlus, Trash2, Link as LinkIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InstallerManagement = () => {
  const navigate = useNavigate();
  const [installers, setInstallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newInstallerName, setNewInstallerName] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    fetchInstallers();
  }, []);

  const fetchInstallers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/installers`);
      setInstallers(response.data);
    } catch (error) {
      console.error("Error fetching installers:", error);
      toast.error("Failed to load installers");
    } finally {
      setLoading(false);
    }
  };

  const handleAddInstaller = async (e) => {
    e.preventDefault();
    
    if (!newInstallerName.trim()) {
      toast.error("Please enter installer name");
      return;
    }

    setIsAdding(true);
    try {
      const response = await axios.post(`${API}/installers`, {
        name: newInstallerName
      });
      
      toast.success(`Installer ${newInstallerName} added successfully!`);
      setNewInstallerName("");
      fetchInstallers();
    } catch (error) {
      console.error("Error adding installer:", error);
      toast.error("Failed to add installer");
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteInstaller = async (installerId, installerName) => {
    if (!window.confirm(`Are you sure you want to delete ${installerName}? Any assigned jobs will be unassigned.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/installers/${installerId}`);
      toast.success(`Installer ${installerName} deleted`);
      fetchInstallers();
    } catch (error) {
      console.error("Error deleting installer:", error);
      toast.error("Failed to delete installer");
    }
  };

  const copyInstallerLink = (linkSlug, installerName) => {
    const link = `${window.location.origin}/installer/${linkSlug}`;
    navigator.clipboard.writeText(link)
      .then(() => {
        toast.success(`Link for ${installerName} copied to clipboard!`);
      })
      .catch(() => {
        toast.error("Failed to copy link");
      });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/h2g")}
                variant="ghost"
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Button>
              <div className="border-l border-gray-300 h-6"></div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Installer Management
                </h1>
                <p className="text-sm text-gray-600">
                  Add or remove installers
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/assignments")}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Go to Assignments
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        
        {/* Add New Installer */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Add New Installer</h2>
          <form onSubmit={handleAddInstaller} className="flex gap-3">
            <input
              type="text"
              value={newInstallerName}
              onChange={(e) => setNewInstallerName(e.target.value)}
              placeholder="Enter installer name"
              className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
            />
            <button
              type="submit"
              disabled={isAdding}
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <UserPlus className="w-5 h-5" />
              {isAdding ? "Adding..." : "Add Installer"}
            </button>
          </form>
        </div>

        {/* Installers List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            All Installers ({installers.length})
          </h2>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="text-gray-500 mt-2">Loading installers...</p>
            </div>
          ) : installers.length === 0 ? (
            <div className="text-center py-12">
              <UserPlus className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No installers added yet</p>
              <p className="text-sm text-gray-400 mt-1">Add your first installer above</p>
            </div>
          ) : (
            <div className="space-y-3">
              {installers.map((installer) => (
                <div
                  key={installer.id}
                  className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900">
                        {installer.name}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Link: /installer/{installer.link_slug}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => copyInstallerLink(installer.link_slug, installer.name)}
                        className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-2"
                      >
                        <LinkIcon className="w-4 h-4" />
                        Copy Link
                      </button>
                      <button
                        onClick={() => handleDeleteInstaller(installer.id, installer.name)}
                        className="px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallerManagement;
